import { SUPPORTED_CHANNELS } from './CopyGenerationOptions.types';
import type { CopyOptions } from './CopyGenerationOptions.types';

const DEFAULT_TOP_P = 1;
const DEFAULT_MAX_OUTPUT_TOKENS = 20000;

/**
 * Seeded into the store on mount so the backend never falls back to its own defaults, but only by
 * V1, whose popup renders inline in the composer. V2's lazy-mounts, and by the time the user opens
 * it `ChatInputV2` has usually written `llmOptions.model` into the bag, which skips the seed. These
 * match the schema's own defaults, so V2 loses nothing by skipping them; a value that stopped
 * matching would not reach V2.
 */
export const getDefaultCopyOptions = ({ model }: Pick<CopyOptions, 'model'>): CopyOptions => ({
  model,
  channels: [SUPPORTED_CHANNELS[0].value],
  responseStyle: 'balanced',
  variations: 1,
  knowledgeBaseIds: [],
  enableQAFeedback: false,
});

const TEMPERATURE_BY_RESPONSE_STYLE: Record<CopyOptions['responseStyle'], number> = {
  focused: 0.1,
  balanced: 0.5,
  imaginative: 0.8,
};

/**
 * Projects the popup's selections into what gets stored and sent as the agent's input.
 *
 * Shared by both variants of the popup so the rules below hold whichever one the flag renders.
 *
 * `options` is spread first so sibling keys the popup does not own survive an update:
 * `projectContext`, written into the same bag by the context picker. `llmOptions` is not among them:
 * it is rebuilt below from the popup's own `model`, which is what V1 needs, since V1 owns the model
 * dropdown. Under V2 the toolbar chip is the model control instead, so a chip selection is replaced
 * by the popup's `model` on the next popup interaction.
 *
 * An empty `goals` is dropped rather than sent, because the schema declares it optional but refuses
 * `''`, and the input is checked by a guardrail that trips the whole turn: an objective the user
 * typed and then cleared would fail their next message.
 *
 * Only `''`, not whitespace. This output is what the field renders, so treating a lone space as
 * absent would erase it the moment the user typed it.
 */
export const toStoreCopyOptions = ({ goals, ...options }: CopyOptions) => ({
  ...options,
  ...(goals ? { goals } : {}),
  llmOptions: {
    temperature: TEMPERATURE_BY_RESPONSE_STYLE[options.responseStyle],
    model: options.model,
    topP: DEFAULT_TOP_P,
    maxOutputTokens: DEFAULT_MAX_OUTPUT_TOKENS,
  },
});
